import { db } from './firebase'
import {
    doc,
    getDoc,
    setDoc,
    updateDoc,
    collection,
    query,
    where,
    getDocs,
    deleteDoc,
    serverTimestamp,
    orderBy,
    Timestamp
} from 'firebase/firestore'

// Collection reference
const USERS_COLLECTION = 'users'
const REGISTRATION_ATTEMPTS_COLLECTION = 'registrationAttempts'

/**
 * Get user profile from Firestore
 */
export async function getUserProfile(userId) {
    const docRef = doc(db, USERS_COLLECTION, userId)
    const docSnap = await getDoc(docRef)

    if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() }
    }

    return null
}

/**
 * Create new user profile in Firestore
 */
export async function createUserProfile(userId, profileData) {
    const docRef = doc(db, USERS_COLLECTION, userId)
    await setDoc(docRef, {
        ...profileData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
    })
}

/**
 * Update user profile
 */
export async function updateUserProfile(userId, updates) {
    const docRef = doc(db, USERS_COLLECTION, userId)
    await updateDoc(docRef, {
        ...updates,
        updatedAt: serverTimestamp()
    })
}

/**
 * Check registration attempts for an email
 * Returns true if blocked (more than 3 attempts in 24h)
 */
export async function checkRegistrationAttempts(email) {
    const attemptsRef = collection(db, REGISTRATION_ATTEMPTS_COLLECTION)
    const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)

    const q = query(
        attemptsRef,
        where('email', '==', email.toLowerCase()),
        where('timestamp', '>=', twentyFourHoursAgo)
    )

    const snapshot = await getDocs(q)
    return snapshot.size >= 3
}

/**
 * Record a registration attempt
 */
export async function recordRegistrationAttempt(email) {
    const attemptsRef = collection(db, REGISTRATION_ATTEMPTS_COLLECTION)
    const newAttemptRef = doc(attemptsRef)

    await setDoc(newAttemptRef, {
        email: email.toLowerCase(),
        timestamp: serverTimestamp(),
        ip: null // Would be set by backend
    })
}

/**
 * Delete user profile from Firestore
 */
export async function deleteUserProfile(userId) {
    const docRef = doc(db, USERS_COLLECTION, userId)
    await deleteDoc(docRef)
}

/**
 * Update user currency preference
 */
export async function updateUserCurrency(userId, currency) {
    return updateUserProfile(userId, { currency })
}
/**
 * Get all user profiles (Admin only)
 * @returns {Promise<Array>}
 */
export async function getAllUsers() {
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    }));
}

/**
 * Update user subscription expiry (Admin only)
 * @param {string} userId
 * @param {Object} duration - e.g., { weeks: 2 }, { months: 1 }, { years: 1 }
 */
export async function updateUserSubscription(userId, { weeks, months, years }) {
    const now = new Date();
    let expiresAt = new Date(now);

    if (weeks) expiresAt.setDate(now.getDate() + (weeks * 7));
    else if (months) expiresAt.setMonth(now.getMonth() + months);
    else if (years) expiresAt.setFullYear(now.getFullYear() + years);

    return updateUserProfile(userId, {
        subscriptionStatus: 'active',
        subscriptionExpiresAt: Timestamp.fromDate(expiresAt),
        role: 'paid' // Automatically upgrade to paid if subscription is set
    });
}

/**
 * Update user history tracking preference (Admin only usually)
 */
export async function updateUserHistoryPreference(userId, saveHistory) {
    const docRef = doc(db, USERS_COLLECTION, userId)
    await updateDoc(docRef, {
        saveHistory,
        updatedAt: serverTimestamp()
    })
}
